import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";

import "./AssessmentResultModal.scss";

const IconCalendar = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="18" rx="2" />
        <path d="M16 2v4M8 2v4M3 10h18" />
    </svg>
);

const IconClock = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
    </svg>
);

const IconFlag = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 22V4a2 2 0 012-2h9l-1 5 1 5H6" />
    </svg>
);

const IconCode = () => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="16 18 22 12 16 6" />
        <polyline points="8 6 2 12 8 18" />
    </svg>
);

const stripHtml = (s) =>
    String(s || "").replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim();

const optionState = (opt) => {
    if (opt.score > 0) return "correct";
    if (opt.answered) return "wrong";
    return "plain";
};

const optionStateText = (opt) => {
    if (opt.score > 0) return "Correct";
    if (opt.answered) return "Wrong";
    return "Not answered";
};

const fmtScoreLabel = (v) => Number(v).toFixed(1).replace(/\.0$/, "");

function QuestionCard({ q, num }) {
    const qScore = q.score != null ? fmtScoreLabel(q.score) : "—";

    let statusPill = null;
    if (q.options && q.options.length) {
        const answered = q.options.filter((o) => o.answered);
        const correct = q.options.filter((o) => o.score > 0);
        if (!answered.length) {
            statusPill = <span className="b-pill qstatus nopick">Unanswered</span>;
        } else if (answered.length === correct.length && correct.every((o) => o.answered)) {
            statusPill = <span className="b-pill qstatus good">{answered.length}/{correct.length} Correct</span>;
        } else {
            statusPill = <span className="b-pill qstatus bad">Wrong</span>;
        }
    }

    let optionsHtml = null;
    if (q.options && q.options.length) {
        const inputType = q.questiontype === 2 ? "radio" : "checkbox";
        optionsHtml = (
            <div className="options">
                {q.options.map((opt) => (
                    <label className={`option ${optionState(opt)}`} key={opt.id}>
                        <input type={inputType} name={`q${q.id}`} disabled defaultChecked={opt.answered} />
                        <span className="box" aria-hidden="true"></span>
                        <span className="option-text">{opt.optionname}</span>
                        <span className="state">{optionStateText(opt)}</span>
                    </label>
                ))}
            </div>
        );
    }

    let freeAnswerHtml = null;
    if (q.questiontype === 3 && q.singleinputanswer) {
        freeAnswerHtml = (
            <div className="free-answer">
                <span className="label">Your answer</span>
                <textarea readOnly value={stripHtml(q.singleinputanswer)} />
            </div>
        );
    }

    return (
        <div className="q-card" id={`ar-q-${num}`} style={{ "--i": num - 1 }}>
            <div className="q-head">
                <span className="q-num">{num}</span>
                <div className="q-text">{stripHtml(q.questionname) || "(No question text)"}</div>
                <div className="q-badges">
                    {statusPill}
                    {/* <span className="b-pill qscore">Score {qScore}</span> */}
                </div>
            </div>
            {num === 1 && (
                <div className="q-image">
                    <img src={`${process.env.PUBLIC_URL}/static/images/py-img.png`} alt="" />
                </div>
            )}
            {optionsHtml}
            {freeAnswerHtml}
        </div>
    );
}

export default function AssessmentResultModal({ show, onClose, record }) {
    const barRef = useRef(null);
    const ringRef = useRef(null);
    const [activeTab, setActiveTab] = useState("answers");
    const [saveState, setSaveState] = useState({ notes: false });

    useEffect(() => {
        if (show) {
            setActiveTab("answers");
            setSaveState({ notes: false });
        }
    }, [show]);

    const { answers, sectionName, pct, questions } = useMemo(() => {
        if (!record) return { answers: null, sectionName: "Assessment", pct: 0, questions: [] };
        let parsed = null;
        try {
            parsed = JSON.parse(record.answers || "null");
        } catch (e) {
            parsed = null;
        }
        const secs = (parsed && Array.isArray(parsed.sections)) ? parsed.sections : [];
        const name = (secs[0] && secs[0].sectionname) || record.subjectname || "Assessment";
        const p = record.totalscore ? Math.round((record.score / record.totalscore) * 100) : 0;
        const qs = [];
        secs.forEach((section) => (section.questions || []).forEach((q) => {
            qs.push({ ...q, num: qs.length + 1 });
        }));
        return { answers: parsed, sectionName: name, pct: p, questions: qs };
    }, [record]);

    useEffect(() => {
        if (!show || !record || activeTab !== "answers") return;
        const circ = 2 * Math.PI * 32;
        const t = setTimeout(() => {
            if (barRef.current) barRef.current.style.width = `${pct}%`;
            if (ringRef.current) ringRef.current.style.strokeDashoffset = circ * (1 - pct / 100);
        }, 80);
        return () => clearTimeout(t);
    }, [show, record, activeTab, pct]);

    if (!record) return null;

    const hasValidAnswers = !!(answers && Array.isArray(answers.sections));
    const candName = [record.firstname, record.lastname].filter(Boolean).join(" ") || "Candidate";

    const handleSave = (key) => {
        setSaveState((prev) => ({ ...prev, [key]: true }));
        setTimeout(() => setSaveState((prev) => ({ ...prev, [key]: false })), 1600);
    };

    const scrollToQuestion = (num) => {
        const el = document.getElementById(`ar-q-${num}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const answersPanel = (
        <section className="tab-panel">
            <section className="summary-grid">
                {/* <div className="summary-score">
                    <div className="score-ring">
                        <svg viewBox="0 0 76 76" aria-hidden="true">
                            <defs>
                                <linearGradient id="arRingGrad" x1="0" y1="0" x2="1" y2="1">
                                    <stop offset="0%" stopColor="blue" />
                                    <stop offset="100%" stopColor="#2418fd9e" />
                                </linearGradient>
                            </defs>
                            <circle className="ring-bg" cx="38" cy="38" r="32" />
                            <circle
                                ref={ringRef}
                                className="ring-fill"
                                cx="38"
                                cy="38"
                                r="32"
                                stroke="url(#arRingGrad)"
                                strokeDasharray="201"
                                strokeDashoffset="201"
                            />
                        </svg>
                        <div className="score-num">
                            {fmtScoreLabel(record.score)}
                            <small>/{record.totalscore || "—"}</small>
                        </div>
                        <div className="score-pct">{pct}%</div>
                    </div>
                </div>
                <div className="summary-meta">
                    <h3>{record.interpretation || "Assessment Result"}</h3>
                    <div className="role">{record.severity || ""}</div>
                    <div className="summary-chips">
                        <span className="badge severity">{record.severity || "—"}</span>
                        <span className="badge status">{record.resultstatus || "—"}</span>
                    </div>
                    <div className="score-bar-track">
                        <div ref={barRef} className="score-bar-fill" style={{ width: 0 }}></div>
                    </div>
                </div> */}

                
                <div className="summary-meta-icons">
                    <span><IconCalendar />{record.rundate || "—"}</span>
                    {record.starttime && <span><IconClock />Start {record.starttime}</span>}
                    {record.endtime && <span><IconClock />End {record.endtime}</span>}
                    <span><IconFlag />{record.runstatus || "—"}</span>
                </div>
            </section>

            {questions.length > 0 && (
                <nav className="questions-nav" aria-label="Jump to question">
                    <span className="qn-label">On this page</span>
                    <div className="qn-chips">
                        {questions.map((q) => (
                            <button type="button" className="chip" key={q.num} onClick={() => scrollToQuestion(q.num)}>
                                {q.num}
                            </button>
                        ))}
                    </div>
                </nav>
            )}

            <section className="questions">
                {questions.length > 0
                    ? questions.map((q) => <QuestionCard key={q.num} q={q} num={q.num} />)
                    : <div className="error-box">No question data available in this result.</div>}
            </section>

            <section className="notes-card">
                <label className="label" htmlFor="arNotesInput">Enter Answer Notes</label>
                <textarea id="arNotesInput" placeholder=""></textarea>
                <div className="notes-actions">
                    <button
                        type="button"
                        className={`save-btn ${saveState.notes ? "saved" : ""}`}
                        onClick={() => handleSave("notes")}
                    >
                        {saveState.notes ? "Saved" : "Save Answer Notes"}
                    </button>
                </div>
            </section>
        </section>
    );

    return (
        <Modal
            show={show}
            onHide={onClose}
            centered
            dialogClassName="ar-dialog"
            className="assessment-result-modal"
        >
            <Modal.Header className="ar-header">
                <Modal.Title className="ar-title">Assessment Result</Modal.Title>
                <button type="button" className="ar-close-btn" onClick={onClose} aria-label="Close">
                    <i className="fa fa-times mr-2"></i>
                </button>
            </Modal.Header>
            <Modal.Body className="ar-body">
                {hasValidAnswers ? (
                    <div className="ar-root">
                        <div className="assessee">
                            <span className="assessee-label">Assessee</span>
                            <span>
                                {candName}
                                {record.clientnumber ? ` | ${record.clientnumber}` : ""}
                                {record.subjectname ? ` | ${record.subjectname}` : ""}
                            </span>
                        </div>

                        <nav className="tabs" role="tablist" aria-label="Result sections">
                            <button
                                type="button"
                                className={`tab ${activeTab === "answers" ? "active" : ""}`}
                                onClick={() => setActiveTab("answers")}
                            >
                                Results
                            </button>
                            <button
                                type="button"
                                className={`tab ${activeTab === "outcomes" ? "active" : ""}`}
                                onClick={() => setActiveTab("outcomes")}
                            >
                                Outcomes
                            </button>
                        </nav>

                        <section className="title-block">
                            <h2 className="assessment-title">{record.assessmentname || "Assessment Result"}</h2>
                            <div className="subject-banner">
                                <IconCode />
                                <span>{sectionName}</span>
                            </div>
                        </section>

                        {activeTab === "outcomes" ? (
                            <section className="tab-panel">
                                <div className="error-box">Sections are under construction.</div>
                            </section>
                        ) : (
                            answersPanel
                        )}
                    </div>
                ) : (
                    <div className="error-box">No question data available in this result.</div>
                )}
            </Modal.Body>
        </Modal>
    );
}
